import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { api, useAuth } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Wallet, TrendingUp, TrendingDown, PiggyBank, Gift, Calendar, 
  ArrowUpRight, ArrowDownRight, RefreshCw, Clock, DollarSign,
  BarChart3, Activity, Zap, History, LineChart, Users, Trophy,
  Share2, Copy, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  LineChart as RechartsLineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';

export default function PortfolioPage() {
  const { user } = useAuth();
  const { formatCurrency } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [portfolio, setPortfolio] = useState(null);
  const [dailyEarnings, setDailyEarnings] = useState([]);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchPortfolioData();
    const interval = setInterval(fetchPortfolioData, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const fetchPortfolioData = async () => {
    try {
      const [summaryRes, earningsRes, statsRes, historyRes, referralRes, leaderboardRes] = await Promise.all([
        api.get('/portfolio/summary'),
        api.get('/portfolio/daily-earnings?days=7'),
        api.get('/portfolio/stats'),
        api.get('/portfolio/history?limit=20'),
        api.get('/referral/stats'),
        api.get('/referral/leaderboard?limit=10')
      ]);
      
      setPortfolio(summaryRes.data);
      setDailyEarnings(earningsRes.data.daily_earnings || []);
      setStats(statsRes.data);
      setHistory(historyRes.data);
      setReferralStats(referralRes.data);
      setLeaderboard(leaderboardRes.data.leaderboard || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch portfolio:', error);
      setLoading(false);
    }
  };

  const copyReferralCode = () => {
    if (referralStats?.referral_code) {
      navigator.clipboard.writeText(referralStats.referral_code);
      setCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'holdings', label: 'Holdings', icon: PiggyBank },
    { id: 'earnings', label: 'Daily Earnings', icon: Calendar },
    { id: 'referrals', label: 'Referrals', icon: Users },
    { id: 'history', label: 'History', icon: History }
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-[#00E676]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="portfolio-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Your Portfolio
            </h1>
            <p className="text-gray-400 mt-1">Real-time investment tracking</p>
          </div>
          <Button 
            onClick={fetchPortfolioData}
            variant="outline"
            className="border-white/10"
            data-testid="refresh-portfolio"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Net Worth */}
          <Card className="card-dashboard border-[#00E676]/30 bg-gradient-to-br from-[#00E676]/10 to-transparent">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Net Worth</p>
                  <p className="text-2xl font-bold text-[#00E676]" data-testid="net-worth">
                    {formatCurrency(portfolio?.net_worth || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-[#00E676]/20 flex items-center justify-center">
                  <Wallet className="w-6 h-6 text-[#00E676]" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cash Balance */}
          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Cash Balance</p>
                  <p className="text-2xl font-bold text-white" data-testid="cash-balance">
                    {formatCurrency(portfolio?.balance || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total Invested */}
          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Total Invested</p>
                  <p className="text-2xl font-bold text-white" data-testid="total-invested">
                    {formatCurrency(portfolio?.total_invested || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <PiggyBank className="w-6 h-6 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total Profit */}
          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Total Profit</p>
                  <p className={`text-2xl font-bold ${(portfolio?.total_profit || 0) >= 0 ? 'text-[#00E676]' : 'text-red-500'}`} data-testid="total-profit">
                    {(portfolio?.total_profit || 0) >= 0 ? '+' : ''}{formatCurrency(portfolio?.total_profit || 0)}
                  </p>
                  <p className={`text-xs ${(portfolio?.total_profit_pct || 0) >= 0 ? 'text-[#00E676]' : 'text-red-500'}`}>
                    {(portfolio?.total_profit_pct || 0) >= 0 ? '+' : ''}{(portfolio?.total_profit_pct || 0).toFixed(2)}%
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${(portfolio?.total_profit || 0) >= 0 ? 'bg-[#00E676]/20' : 'bg-red-500/20'}`}>
                  {(portfolio?.total_profit || 0) >= 0 ? (
                    <TrendingUp className="w-6 h-6 text-[#00E676]" />
                  ) : (
                    <TrendingDown className="w-6 h-6 text-red-500" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Cashback Card */}
        {(portfolio?.total_cashback || 0) > 0 && (
          <Card className="card-dashboard border-[#F7931A]/30 bg-gradient-to-r from-[#F7931A]/10 to-transparent">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#F7931A]/20 flex items-center justify-center">
                    <Gift className="w-5 h-5 text-[#F7931A]" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">Investment Cashback Earned</p>
                    <p className="text-sm text-gray-400">5% cashback on every investment</p>
                  </div>
                </div>
                <p className="text-xl font-bold text-[#F7931A]" data-testid="total-cashback">
                  +{formatCurrency(portfolio?.total_cashback || 0)}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              className={activeTab === tab.id ? 'bg-[#00E676] text-black' : 'border-white/10'}
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Stats */}
            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Activity className="w-5 h-5 text-[#00E676]" />
                  Trading Statistics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg bg-white/5">
                    <p className="text-sm text-gray-400">Total Trades</p>
                    <p className="text-xl font-bold text-white">{stats?.total_trades || 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-white/5">
                    <p className="text-sm text-gray-400">Win Rate</p>
                    <p className="text-xl font-bold text-[#00E676]">{(stats?.win_rate || 0).toFixed(1)}%</p>
                  </div>
                  <div className="p-3 rounded-lg bg-white/5">
                    <p className="text-sm text-gray-400">AI Trades</p>
                    <p className="text-xl font-bold text-purple-400">{stats?.ai_trades || 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-white/5">
                    <p className="text-sm text-gray-400">Manual Trades</p>
                    <p className="text-xl font-bold text-blue-400">{stats?.manual_trades || 0}</p>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-white/10">
                  <p className="text-sm text-gray-400 mb-2">Realized Profit</p>
                  <p className={`text-2xl font-bold ${(stats?.total_realized_profit || 0) >= 0 ? 'text-[#00E676]' : 'text-red-500'}`}>
                    {(stats?.total_realized_profit || 0) >= 0 ? '+' : ''}{formatCurrency(stats?.total_realized_profit || 0)}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Best/Worst Trade */}
            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <LineChart className="w-5 h-5 text-[#F7931A]" />
                  Trade Highlights
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {stats?.best_trade && (
                  <div className="p-4 rounded-xl bg-[#00E676]/10 border border-[#00E676]/30">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-gray-400">Best Trade</p>
                      <ArrowUpRight className="w-4 h-4 text-[#00E676]" />
                    </div>
                    <p className="text-lg font-bold text-[#00E676]">
                      +{formatCurrency(stats.best_trade.profit || 0)}
                    </p>
                    <p className="text-sm text-gray-400">
                      {stats.best_trade.business_name || stats.best_trade.business_id}
                    </p>
                  </div>
                )}
                
                {stats?.worst_trade && (
                  <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-gray-400">Worst Trade</p>
                      <ArrowDownRight className="w-4 h-4 text-red-500" />
                    </div>
                    <p className="text-lg font-bold text-red-500">
                      {formatCurrency(stats.worst_trade.profit || 0)}
                    </p>
                    <p className="text-sm text-gray-400">
                      {stats.worst_trade.business_name || stats.worst_trade.business_id}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'holdings' && (
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-white">Your Holdings ({portfolio?.holdings_count || 0})</CardTitle>
            </CardHeader>
            <CardContent>
              {portfolio?.holdings?.length > 0 ? (
                <div className="space-y-3">
                  {portfolio.holdings.map((holding) => (
                    <div 
                      key={holding.business_id}
                      className="p-4 rounded-xl bg-white/5 border border-white/10 hover:border-[#00E676]/30 transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <img 
                            src={holding.logo_url} 
                            alt={holding.name}
                            className="w-10 h-10 rounded-lg object-cover"
                          />
                          <div>
                            <p className="font-semibold text-white">{holding.name}</p>
                            <p className="text-sm text-gray-400">
                              {holding.shares.toFixed(4)} shares @ {formatCurrency(holding.entry_price)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-white">{formatCurrency(holding.current_value)}</p>
                          <p className={`text-sm ${holding.profit >= 0 ? 'text-[#00E676]' : 'text-red-500'}`}>
                            {holding.profit >= 0 ? '+' : ''}{formatCurrency(holding.profit)} ({holding.profit_pct >= 0 ? '+' : ''}{holding.profit_pct.toFixed(2)}%)
                          </p>
                        </div>
                      </div>
                      {holding.ai_trade && (
                        <div className="mt-2 flex items-center gap-1 text-xs text-purple-400">
                          <Zap className="w-3 h-3" />
                          AI Trade
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <PiggyBank className="w-12 h-12 mx-auto text-gray-500 mb-4" />
                  <p className="text-gray-400">No holdings yet</p>
                  <p className="text-sm text-gray-500">Start investing in small businesses to build your portfolio</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'earnings' && (
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#00E676]" />
                Daily Earnings (Last 7 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dailyEarnings.length > 0 ? (
                <>
                  <div className="h-64 mb-6">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={dailyEarnings.slice().reverse()}>
                        <defs>
                          <linearGradient id="colorEarnings" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#00E676" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#00E676" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis dataKey="date" stroke="#888" fontSize={12} />
                        <YAxis stroke="#888" fontSize={12} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                          labelStyle={{ color: '#fff' }}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="total_earnings" 
                          stroke="#00E676" 
                          fillOpacity={1} 
                          fill="url(#colorEarnings)" 
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  
                  <div className="space-y-2">
                    {dailyEarnings.map((day) => (
                      <div 
                        key={day.date}
                        className="flex items-center justify-between p-3 rounded-lg bg-white/5"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded bg-white/10 flex items-center justify-center">
                            <Calendar className="w-4 h-4 text-gray-400" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{day.date}</p>
                            <p className="text-xs text-gray-500">{day.trades} trades</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-semibold ${day.total_earnings >= 0 ? 'text-[#00E676]' : 'text-red-500'}`}>
                            {day.total_earnings >= 0 ? '+' : ''}{formatCurrency(day.total_earnings)}
                          </p>
                          {day.cashback > 0 && (
                            <p className="text-xs text-[#F7931A]">+{formatCurrency(day.cashback)} cashback</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 mx-auto text-gray-500 mb-4" />
                  <p className="text-gray-400">No earnings data yet</p>
                  <p className="text-sm text-gray-500">Start trading to see your daily earnings</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'referrals' && (
          <div className="space-y-6">
            {/* Referral Tier Card */}
            <Card className="card-dashboard border-[#F7931A]/30 bg-gradient-to-r from-[#F7931A]/10 to-transparent">
              <CardContent className="py-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center text-3xl">
                      {referralStats?.tier?.badge || '🌱'}
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Your Tier</p>
                      <p className="text-2xl font-bold" style={{ color: referralStats?.tier?.color || '#fff' }}>
                        {referralStats?.tier?.name || 'Starter'}
                      </p>
                      <p className="text-sm text-gray-400">
                        {referralStats?.total_referrals || 0} referrals • ${referralStats?.total_bonus || 0} earned
                      </p>
                    </div>
                  </div>
                  {referralStats?.next_tier && (
                    <div className="flex-1 max-w-xs">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-400">Next: {referralStats.next_tier.name} {referralStats.next_tier.badge}</span>
                        <span className="text-white">{referralStats.next_tier.referrals_needed} more</span>
                      </div>
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[#F7931A] rounded-full transition-all"
                          style={{ width: `${referralStats.next_tier.progress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Referral Code & Share */}
            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-[#00E676]" />
                  Your Referral Code
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 relative">
                    <Input
                      value={referralStats?.referral_code || ''}
                      readOnly
                      className="input-dark text-lg font-mono pr-12"
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      className="absolute right-2 top-1/2 -translate-y-1/2"
                      onClick={copyReferralCode}
                    >
                      {copied ? <Check className="w-4 h-4 text-[#00E676]" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <Button 
                    className="bg-[#00E676] text-black hover:bg-[#00E676]/80"
                    onClick={() => {
                      const url = `${window.location.origin}/register?ref=${referralStats?.referral_code}`;
                      navigator.clipboard.writeText(url);
                      toast.success('Referral link copied!');
                    }}
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share Link
                  </Button>
                </div>
                <div className="mt-4 p-4 rounded-xl bg-white/5 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{referralStats?.total_referrals || 0}</p>
                    <p className="text-xs text-gray-400">Total Referrals</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[#00E676]">{referralStats?.referrals_invested || 0}</p>
                    <p className="text-xs text-gray-400">Invested</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[#F7931A]">${referralStats?.bonus_breakdown?.signup_bonuses || 0}</p>
                    <p className="text-xs text-gray-400">Signup Bonuses</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-400">${referralStats?.bonus_breakdown?.investment_bonuses || 0}</p>
                    <p className="text-xs text-gray-400">Investment Bonuses</p>
                  </div>
                </div>
                <p className="mt-3 text-xs text-gray-500 text-center">
                  Earn $10 when someone signs up + $10 when they make their first investment = $20 per referral!
                </p>
              </CardContent>
            </Card>

            {/* Leaderboard */}
            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-[#FFD700]" />
                  Top Referrers Leaderboard
                </CardTitle>
              </CardHeader>
              <CardContent>
                {leaderboard.length > 0 ? (
                  <div className="space-y-2">
                    {leaderboard.map((leader, idx) => (
                      <div 
                        key={leader.user_id}
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          leader.user_id === user?.user_id 
                            ? 'bg-[#00E676]/20 border border-[#00E676]/30' 
                            : 'bg-white/5'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                            idx === 0 ? 'bg-[#FFD700] text-black' :
                            idx === 1 ? 'bg-[#C0C0C0] text-black' :
                            idx === 2 ? 'bg-[#CD7F32] text-black' :
                            'bg-white/10 text-gray-400'
                          }`}>
                            {idx < 3 ? ['🥇', '🥈', '🥉'][idx] : leader.rank}
                          </div>
                          <div>
                            <p className="text-white font-medium flex items-center gap-2">
                              {leader.name}
                              <span title={leader.tier.name}>{leader.tier.badge}</span>
                              {leader.user_id === user?.user_id && (
                                <span className="text-xs bg-[#00E676] text-black px-2 py-0.5 rounded">YOU</span>
                              )}
                            </p>
                            <p className="text-xs text-gray-500">
                              {leader.total_referrals} referrals • {leader.invested_referrals} invested
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-[#00E676]">${leader.total_bonus}</p>
                          <p className="text-xs text-gray-500">earned</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Trophy className="w-12 h-12 mx-auto text-gray-500 mb-4" />
                    <p className="text-gray-400">No referrals yet</p>
                    <p className="text-sm text-gray-500">Be the first on the leaderboard!</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tier Explanation */}
            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle className="text-white">Referral Tiers</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                  {[
                    { name: 'Starter', badge: '🌱', min: 0, color: '#9CA3AF' },
                    { name: 'Bronze', badge: '🥉', min: 1, color: '#CD7F32' },
                    { name: 'Silver', badge: '🥈', min: 5, color: '#C0C0C0' },
                    { name: 'Gold', badge: '🥇', min: 15, color: '#FFD700' },
                    { name: 'Platinum', badge: '💎', min: 30, color: '#E5E4E2' },
                    { name: 'Diamond', badge: '💠', min: 50, color: '#B9F2FF' },
                    { name: 'Elite', badge: '👑', min: 100, color: '#9333EA' },
                  ].map((tier) => (
                    <div 
                      key={tier.name}
                      className={`p-3 rounded-xl text-center ${
                        referralStats?.tier?.name === tier.name 
                          ? 'bg-white/20 border border-white/30' 
                          : 'bg-white/5'
                      }`}
                    >
                      <div className="text-2xl mb-1">{tier.badge}</div>
                      <p className="font-semibold" style={{ color: tier.color }}>{tier.name}</p>
                      <p className="text-xs text-gray-500">{tier.min}+ refs</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'history' && (
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <History className="w-5 h-5 text-[#00E676]" />
                Transaction History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {history.length > 0 ? (
                <div className="space-y-2">
                  {history.map((tx, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-3 rounded-lg bg-white/5"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded flex items-center justify-center ${
                          tx.type === 'trade' && tx.action === 'BUY' ? 'bg-blue-500/20' :
                          tx.type === 'trade' && tx.action === 'SELL' ? 'bg-[#00E676]/20' :
                          tx.type === 'deposit' ? 'bg-green-500/20' :
                          tx.type === 'withdrawal' ? 'bg-red-500/20' :
                          'bg-[#F7931A]/20'
                        }`}>
                          {tx.type === 'trade' && tx.action === 'BUY' && <ArrowDownRight className="w-4 h-4 text-blue-400" />}
                          {tx.type === 'trade' && tx.action === 'SELL' && <ArrowUpRight className="w-4 h-4 text-[#00E676]" />}
                          {tx.type === 'deposit' && <DollarSign className="w-4 h-4 text-green-400" />}
                          {tx.type === 'withdrawal' && <Wallet className="w-4 h-4 text-red-400" />}
                          {tx.type === 'cashback' && <Gift className="w-4 h-4 text-[#F7931A]" />}
                        </div>
                        <div>
                          <p className="text-white font-medium">
                            {tx.type === 'trade' ? `${tx.action} ${tx.business_name || ''}` :
                             tx.type === 'deposit' ? 'Deposit' :
                             tx.type === 'withdrawal' ? 'Withdrawal' :
                             'Cashback'}
                          </p>
                          <p className="text-xs text-gray-500">{tx.timestamp?.slice(0, 16).replace('T', ' ')}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold ${
                          tx.type === 'trade' && tx.action === 'BUY' ? 'text-blue-400' :
                          tx.type === 'trade' && tx.action === 'SELL' ? 'text-[#00E676]' :
                          tx.type === 'deposit' ? 'text-green-400' :
                          tx.type === 'withdrawal' ? 'text-red-400' :
                          'text-[#F7931A]'
                        }`}>
                          {tx.type === 'trade' && tx.action === 'BUY' ? '-' : '+'}
                          {formatCurrency(tx.amount || tx.net_amount || 0)}
                        </p>
                        {tx.profit !== undefined && tx.profit !== 0 && (
                          <p className={`text-xs ${tx.profit >= 0 ? 'text-[#00E676]' : 'text-red-500'}`}>
                            P&L: {tx.profit >= 0 ? '+' : ''}{formatCurrency(tx.profit)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <History className="w-12 h-12 mx-auto text-gray-500 mb-4" />
                  <p className="text-gray-400">No transaction history yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
